/**
 * SessionExpiryChip — Live countdown chip + "session expired" warning.
 *
 * Renders nothing until the supplier has explicitly published a TTL via
 * /review `conditions.st` (so no UI noise on Riya / offline / hotel flows).
 * Color transitions: gray → amber (<3 min) → red (<60 s) → red+latched
 * when expired.
 */
import React from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import useSessionExpiry, { formatSecondsAsClock } from '../hooks/useSessionExpiry';

export default function SessionExpiryChip({
  formConditions,
  onExpire,
  testIdSuffix = '',
}) {
  const { secondsRemaining, isExpired, hasSupplierTtl } = useSessionExpiry(formConditions);

  // Fire onExpire exactly once when crossing the boundary.
  const expiredRef = React.useRef(false);
  React.useEffect(() => {
    if (isExpired && !expiredRef.current) {
      expiredRef.current = true;
      onExpire?.();
    }
    if (!isExpired) expiredRef.current = false;
  }, [isExpired, onExpire]);

  if (!hasSupplierTtl) return null;

  const tone = isExpired
    ? 'border-red-300 bg-red-50 text-red-800'
    : secondsRemaining < 60
    ? 'border-red-300 bg-red-50 text-red-800'
    : secondsRemaining < 180
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-gray-200 bg-gray-50 text-gray-700';

  const Icon = isExpired ? AlertTriangle : Clock;

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${tone}`}
      data-testid={`session-expiry-chip${testIdSuffix ? `-${testIdSuffix}` : ''}`}
      role="status"
      aria-live="polite"
    >
      <Icon className="w-3.5 h-3.5" />
      {isExpired
        ? <span>Session expired — please re-search this flight.</span>
        : <span>Session expires in <span className="font-semibold tabular-nums">{formatSecondsAsClock(secondsRemaining)}</span></span>}
    </div>
  );
}
